import { useState, type FormEvent } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CheckCircle2 } from "lucide-react";

const CONVERTKIT_FORM_ID = "9503965";
const CONVERTKIT_ENDPOINT = `https://app.convertkit.com/forms/${CONVERTKIT_FORM_ID}/subscriptions`;

interface ConvertKitHeroFormProps {
  idPrefix: string;
  websitePlaceholder?: string;
}

export function ConvertKitHeroForm({
  idPrefix,
  websitePlaceholder = "https://yoursite.com",
}: ConvertKitHeroFormProps) {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setStatus("submitting");
    setErrorMsg(null);

    const form = e.currentTarget;
    const formData = new FormData(form);

    try {
      const res = await fetch(CONVERTKIT_ENDPOINT, {
        method: "POST",
        body: formData,
        headers: { Accept: "application/json" },
      });
      if (!res.ok) throw new Error(`Request failed (${res.status})`);
      setStatus("success");
      form.reset();
    } catch (err) {
      setStatus("error");
      setErrorMsg(err instanceof Error ? err.message : "Something went wrong. Please try again.");
    }
  }

  if (status === "success") {
    return (
      <div className="mt-6 rounded-2xl bg-primary/10 border border-primary/30 p-6 text-center">
        <CheckCircle2 className="mx-auto h-10 w-10 text-primary" />
        <h3 className="mt-3 text-lg font-bold text-foreground">Success!</h3>
        <p className="mt-2 text-sm text-muted-foreground">
          Check your email to confirm access (check spam just in case).
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="mt-6 space-y-4">
      <div>
        <label htmlFor={`${idPrefix}-name`} className="block text-sm font-medium mb-1.5">
          Name
        </label>
        <Input
          id={`${idPrefix}-name`}
          name="first_name"
          type="text"
          required
          maxLength={100}
          placeholder="Your name"
        />
      </div>
      <div>
        <label htmlFor={`${idPrefix}-email`} className="block text-sm font-medium mb-1.5">
          Email
        </label>
        <Input
          id={`${idPrefix}-email`}
          name="email_address"
          type="email"
          required
          maxLength={255}
          placeholder="you@example.com"
        />
      </div>
      {status === "error" && (
        <p className="text-sm text-destructive">
          {errorMsg ?? "Something went wrong. Please try again."}
        </p>
      )}
      <Button
        type="submit"
        disabled={status === "submitting"}
        className="w-full bg-primary-gradient hover:opacity-90 text-primary-foreground font-semibold tracking-wide rounded-full h-auto min-h-12 py-3 px-4 whitespace-normal text-center leading-snug text-sm sm:text-base"
      >
        {status === "submitting" ? "Submitting…" : "Get More Website Leads (Free)"}
      </Button>
    </form>
  );
}
